import { useState } from "react";

import styles from "./TaskView.module.css";
import { Button } from "../../components/Button";
import { Switch } from "../../components/Switch";
import {
  type ChecksSummary,
  useTaskBuildStatuses,
} from "../../data/api/buildStatuses";
import {
  type ApiPullRequest,
  type ApiPullRequestAttempt,
  mergePullRequest,
  setMergeWhenReady,
  syncPullRequest,
  useTaskPullRequest,
} from "../../data/api/pullRequests";
import { Icon } from "../../icons";

export type PullRequestControlsProps = {
  readonly taskId: string;
  readonly taskStatus: string;
};

// Why the merge gate is closed, for the disabled-button tooltip. `null` means
// nothing is in the way and merge is allowed.
const mergeBlockReason = (
  summary: ChecksSummary,
  conflicted: boolean,
  baseBranch: string,
): string | null => {
  // First, because it is the one the provider itself would refuse: a
  // conflicted branch cannot be merged whatever its checks say, so leaving the
  // button live only buys a round-trip that comes back as an error.
  if (conflicted) {
    return `Conflicts with ${baseBranch} — merge is blocked`;
  }
  switch (summary) {
    case "failing":
      return "Checks are failing — merge is blocked";
    case "none":
      return "No checks have reported yet — merge is blocked";
    case "passing":
      return null;
    case "pending":
      return "Checks are still running — merge is blocked";
  }
};

/**
 * What the "Merge when ready" switch is offering, or waiting for.
 *
 * The gate it describes is deliberately stricter than the Merge button beside
 * it: *every* check that reported has to be green, not only the required ones.
 * Whoever clicks Merge has seen the red optional check and decided about it,
 * and on this path nobody has.
 */
const mergeWhenReadyHint = (armed: boolean, conflicted: boolean): string => {
  if (!armed) {
    return "Merge automatically once every check passes and the branch has no conflicts";
  }
  return conflicted
    ? "Will merge once the conflict clears and every check passes"
    : "Will merge once every check passes";
};

// The error an attempt gave up with, or `null` while it is still trying (or
// has nothing to say).
const attemptError = (attempt: ApiPullRequestAttempt | null): string | null =>
  attempt?.status === "failed" ? (attempt.error ?? "Failed.") : null;

/**
 * What to say about a branch that conflicts with its base, or `null` when it
 * doesn't. Nobody clicked anything to start the rebase — a webhook did — so
 * this is the only place its progress is visible.
 */
const conflictNote = (
  baseBranch: string,
  rebase: ApiPullRequestAttempt | null,
): string => {
  if (rebase?.status === "queued" || rebase?.status === "in_progress") {
    return `Conflicts with ${baseBranch} — rebasing…`;
  }
  const failure = attemptError(rebase);
  return failure
    ? `Conflicts with ${baseBranch} — ${failure}`
    : `Conflicts with ${baseBranch}`;
};

// The PR affordances in the task header: a state pill + "Merge when ready" +
// Merge (both while open) + Refresh, once the agent has opened a pull request.
// Merge is gated on the aggregate checks summary (build-status.md §Merge
// gating) being `passing`, and on the branch not conflicting with its base
// (diffs-and-pull-requests.md §Merge conflicts). The switch beside it arms the
// server to run a stricter version of that gate itself, whenever it opens
// (§Merge when ready) — the button is for a gate that is already open, the
// switch for one that is not.
//
// There is no "Open PR" control. The agent opens its own pull request via the
// `open_pull_request` tool when it judges the work done, so a button asking it
// to was a second way to say the same thing, one the agent was usually already
// acting on. What is left of that path is the reporting: an agent whose attempt
// failed still owes the user the reason.
export const PullRequestControls = ({
  taskId,
  taskStatus,
}: PullRequestControlsProps) => {
  const {
    mergeRequest,
    pullRequest: pr,
    rebaseRequest,
    refetch,
    request,
  } = useTaskPullRequest(taskId);
  const { summary } = useTaskBuildStatuses(taskId);
  // Which action is mid-flight, not merely whether one is: a refresh must not
  // put the Merge button into its merging state, and a refresh that fails must
  // not be reported as a merge that failed.
  const [busy, setBusy] = useState<"arm" | "merge" | "sync" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // The "merge when ready" value we last wrote, against the pull request we
  // wrote it for. Without it the switch waits a round-trip before it moves,
  // which reads as a switch that did nothing; holding it past the next response
  // would ignore a change made in another tab, so it lives exactly that long.
  const [armWrite, setArmWrite] = useState<{
    pr: ApiPullRequest;
    value: boolean;
  } | null>(null);

  // Each of these enqueues a server intent that settles later, so the refetch
  // after it is a guess at when there is something new to see. Two things made
  // that guess indistinguishable from a dead button: a single refetch 1.4s
  // later, which is quicker than a push plus a provider round-trip, and a
  // `.catch()` that discarded the error. What the intent went on to do is
  // reported separately — by `mergeRequest` for a merge, and by realtime for a
  // sync.
  const act = (action: "merge" | "sync", fn: () => Promise<unknown>): void => {
    setBusy(action);
    setActionError(null);
    void fn()
      .then(async () => {
        for (const delay of [700, 1500, 3000]) {
          await new Promise((resolve) => setTimeout(resolve, delay));
          refetch();
        }
      })
      .catch((err: unknown) => {
        setActionError(err instanceof Error ? err.message : "Request failed.");
      })
      .finally(() => setBusy(null));
  };

  if (!pr) {
    // Nothing to report before the agent has produced work, or after the task
    // closed. Otherwise: an open-PR attempt that failed is the one thing worth
    // saying here — the agent asked for a pull request and did not get one.
    //
    // Opening settles asynchronously and can fail for reasons the agent cannot
    // fix by trying again — an expired git credential failing the push with
    // `Invalid username or token` on every attempt. Silence there reads as an
    // agent that simply never got around to opening one.
    const openFailure = attemptError(request);
    if (
      !openFailure ||
      taskStatus === "pending" ||
      taskStatus === "done" ||
      taskStatus === "cancelled"
    ) {
      return null;
    }
    return (
      <span className={styles.rightAction}>
        <span className={styles.prError} role="alert" title={openFailure}>
          Could not open a pull request: {openFailure}
        </span>
      </span>
    );
  }

  // Arming settles inline — the flag is stored before the response — so unlike
  // `act` there is nothing to wait out. The merge it may go on to trigger is
  // still an intent, and reports through `mergeRequest` like a clicked one.
  const arm = (next: boolean): void => {
    setArmWrite({ pr, value: next });
    setActionError(null);
    setBusy("arm");
    void setMergeWhenReady(pr.public_id, next)
      .then(() => refetch())
      .catch((err: unknown) => {
        setArmWrite(null);
        setActionError(err instanceof Error ? err.message : "Request failed.");
      })
      .finally(() => setBusy(null));
  };

  const isOpen = pr.state === "open" || pr.state === "draft";
  const mergeWhenReady =
    armWrite?.pr === pr ? armWrite.value : pr.merge_when_ready;
  // A queued merge intent is one the server accepted and has not settled:
  // the gate re-check and the provider call both still have to happen.
  const merging = busy === "merge" || mergeRequest?.status === "queued";
  // What the merge intent gave up with — which is what "Retry merge" is
  // offering to retry. A POST that never landed (`actionError`) is shown too,
  // but it is not a failed merge: nothing was enqueued to retry.
  const mergeFailure = isOpen ? attemptError(mergeRequest) : null;
  const shownError = actionError ?? mergeFailure;
  const blockReason = mergeBlockReason(summary, pr.conflicted, pr.base_branch);
  const label = (
    <>
      <Icon.PullRequest size={11} /> #{pr.number} · {pr.state}
    </>
  );
  // A conflict is not an error — nothing the user did failed, and the rebase
  // that clears it usually needs nothing from them — so it reads as state
  // rather than as an alert, and yields to a real failure for the same room.
  const conflict =
    isOpen && pr.conflicted && !shownError
      ? conflictNote(pr.base_branch, rebaseRequest)
      : null;
  return (
    <span className={styles.prControls}>
      {/* `title` as well as the text: a provider's refusal is often a wall of
          detail, and the line is clamped. */}
      {shownError ? (
        <span className={styles.prError} role="alert" title={shownError}>
          {shownError}
        </span>
      ) : null}
      {conflict ? (
        <span className={styles.prConflict} title={conflict}>
          {conflict}
        </span>
      ) : null}
      {pr.html_url ? (
        <a
          className={styles.prPill}
          href={pr.html_url}
          rel="noreferrer"
          target="_blank"
        >
          {label}
          <Icon.External size={10} />
        </a>
      ) : (
        <span className={styles.prPill}>{label}</span>
      )}
      {isOpen ? (
        <>
          {/* Beside Merge, not instead of it: the switch stays available while
              the button is disabled, which is the whole window it is for. */}
          <span
            className={styles.mergeWhenReady}
            title={mergeWhenReadyHint(mergeWhenReady, pr.conflicted)}
          >
            <Switch
              aria-label="Merge when ready"
              checked={mergeWhenReady}
              disabled={busy === "arm"}
              onCheckedChange={arm}
            />
            Merge when ready
          </span>
          <Button
            disabled={merging || blockReason !== null}
            onClick={() => act("merge", () => mergePullRequest(pr.public_id))}
            size="sm"
            title={
              merging
                ? "Merging…"
                : (blockReason ??
                  (mergeFailure
                    ? "Try the merge again"
                    : "Merge this pull request"))
            }
            variant="accent"
          >
            {merging ? "Merging…" : mergeFailure ? "Retry merge" : "Merge"}
          </Button>
        </>
      ) : null}
      <Button
        disabled={busy !== null}
        icon
        onClick={() => act("sync", () => syncPullRequest(pr.public_id))}
        size="sm"
        title="Refresh from GitHub"
      >
        <Icon.Refresh size={12} />
      </Button>
    </span>
  );
};
